import React, { useState, useEffect, useCallback } from 'react';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import {
  CenterListingByTypeProps,
  CenterTypeProps,
  SalesforceModelBase,
  CenterTypeColumnProps,
} from 'lib/salesforce/types';
import axios from 'axios';
import classnames from 'classnames';
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';
import TableHeading from 'components/authorable/Ritn/ParticipatingCenter/Icon/TableHeading';
import ReactPaginate from 'react-paginate';
import fnNoOp from 'lib/util/no-op';
import { logError, makeLoggableError } from 'lib/util/errors';
import Button from 'components/helpers/Button';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';

const tdClass = classnames('p-2.5', 'border', 'border-gray-quartz');
const trClass = classnames(
  'p-2.5',
  'even:bg-gray-whisper',
  'mb-2',
  'sm:mb-0',
  'border',
  'border-gray-quartz'
);
const thClass = classnames('p-2.5', 'border', 'border border-gray-quartz');

export type GetColumnValueProps<T extends SalesforceModelBase> = {
  name: string;
  item: T;
};

const buildCenterRows = <T extends SalesforceModelBase>({
  currentItems,
  columnNames,
  GetColumnValue,
}: {
  currentItems: T[];
  columnNames: CenterTypeColumnProps[];
  GetColumnValue: (props: GetColumnValueProps<T>) => React.JSX.Element;
}) => {
  return currentItems.map((item) => {
    return (
      <Tr key={item.Id} className={trClass}>
        {columnNames.map((column) => {
          const fieldName = column?.fields?.salesForceField?.name;
          return (
            <Td key={column?.id} className={tdClass}>
              {fieldName && <GetColumnValue name={fieldName} item={item} />}
            </Td>
          );
        })}
      </Tr>
    );
  });
};

const errorModal = ({ reason }: { reason: string }) => {
  return (
    <>
      <div className="absolute left-1/2 top-1/3 z-[9999] -translate-x-1/2 -translate-y-1/2">
        <div className="grid grid-flow-col grid-rows-2 justify-items-center rounded-lg border-0 bg-white p-7 shadow-lg outline-none focus:outline-none">
          <div>
            <h2>Error Occurred</h2>
            <p>{reason}</p>
          </div>
          <div className={classnames('py-2')}>
            <Button
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.location.reload();
                }
              }}
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

type SalesforceTableProps<T extends SalesforceModelBase> = CenterListingByTypeProps & {
  getCenterSorterForColumn: (name: string) => ((a: T, b: T) => number) | undefined;
  getShouldSort: (fieldName: string) => boolean;
  onDataLoaded?: (data: T[]) => T[];
  GetColumnValue: (props: GetColumnValueProps<T>) => React.JSX.Element;
  apiUrl: string;
  defaultSortColumn: string;
};

const SalesforceTable = <T extends SalesforceModelBase>({
  fields,
  getCenterSorterForColumn,
  apiUrl,
  onDataLoaded,
  getShouldSort,
  GetColumnValue,
  defaultSortColumn,
}: SalesforceTableProps<T>): JSX.Element => {
  const centerType = fields?.centerType as CenterTypeProps;
  const centerTypeName = centerType?.fields?.centerTypeName?.value;
  const columnNames = fields?.centerTypeTableColumns as CenterTypeColumnProps[];
  const [tableData, setTableData] = useState<T[]>([]);
  const [currentItems, setCurrentItems] = useState<T[]>(tableData);
  const [sortIndicator, setIndicator] = useState({
    columName: '',
    isAscending: false,
  });
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [hadUnexpectedError, setHadUnexpectedError] = useState(false);
  const [errorReason, seterrorReason] = useState<string>('');

  const pageSize = fields?.paginationSize.value ? fields?.paginationSize.value : 10;
  const displayPagination = tableData.length > pageSize;
  const isExperienceEditor = useExperienceEditor();

  useEffect(() => {
    const endOffset = itemOffset + pageSize;
    const copyOfprevCenterList = [...tableData];
    const newCurrentData = copyOfprevCenterList.slice(itemOffset, endOffset);
    setCurrentItems(newCurrentData);
    setPageCount(Math.ceil(copyOfprevCenterList.length / pageSize));
  }, [itemOffset, tableData, pageSize]);

  const handlePageClick = useCallback(
    (event: { selected: number }) => {
      const newOffset = (event.selected * pageSize) % tableData.length;
      setItemOffset(newOffset);
    },
    [tableData, setItemOffset, pageSize]
  );

  const sortTable = useCallback(
    (column: string, isAscending: boolean) => {
      setTableData((prevCenterList) => {
        const sortedCenterList = [...prevCenterList];
        const sortFn = getCenterSorterForColumn(column);
        if (sortFn) {
          sortedCenterList.sort(sortFn);
        }
        setIndicator({
          columName: column,
          isAscending: isAscending,
        });
        return isAscending ? sortedCenterList : sortedCenterList.reverse();
      });
      return 1; // Return a number to indicate the sort was performed
    },
    [getCenterSorterForColumn, setTableData, setIndicator]
  );

  useEffect(() => {
    if (!centerTypeName) {
      return fnNoOp;
    }
    const abortController = new AbortController();
    const abortControllerSignal = abortController.signal;
    let requestProcessed = false;
    const centerTypeData = async () => {
      const formData = new FormData();
      formData.append('centerTypeName', centerTypeName);
      const response = await axios.post<T[] | undefined>(apiUrl, formData, {
        signal: abortControllerSignal,
      });
      requestProcessed = true;
      if (response.data) {
        const modelList = onDataLoaded ? onDataLoaded(response.data) : response.data;
        setTableData(modelList);

        if (columnNames.length > 0) {
          setIndicator({
            columName: defaultSortColumn,
            isAscending: true,
          });
        }
      }
    };

    centerTypeData().catch((error) => {
      requestProcessed = true;
      if (axios.isCancel(error)) {
        return;
      }
      if (axios.isAxiosError(error)) {
        logError('SalesforceTable:LoadData', error, { apiUrl });
        seterrorReason((error.response?.data as string) ?? 'Unexpected Failure');
      } else {
        logError('SalesforceTable:LoadData', makeLoggableError(error), { apiUrl });
        seterrorReason('Unexpected Failure');
      }
      setHadUnexpectedError(true);
    });

    return () => {
      if (requestProcessed) {
        return;
      }
      abortController.abort();
    };
  }, [centerTypeName, columnNames, onDataLoaded, apiUrl, defaultSortColumn]);

  if (isExperienceEditor) {
    return <div>SalesForce Table is not supported on Experience Editor</div>;
  }

  if (!fields) {
    return <MissingDataSource />;
  }

  const tableHeaders = columnNames.map((column) => {
    if (!column?.fields) {
      return <></>;
    }
    const fieldName = column.fields.salesForceField?.name;
    return (
      <Th key={column.id ?? column.fields.columnText.value} className={thClass}>
        <TableHeading
          columnKey={column.fields.salesForceField?.name}
          label={column.fields.columnText.value}
          sortTable={sortTable}
          sortIndicator={sortIndicator}
          showSortIcon={!!fieldName && getShouldSort(fieldName)}
        />
      </Th>
    );
  });

  return (
    <div data-component="authorable/general/CenterListingByType">
      {hadUnexpectedError && errorModal({ reason: errorReason })}
      {!hadUnexpectedError && (
        <>
          <Table className="hide-header-in-mobile">
            <Thead>
              <Tr className={classnames('text-left', 'font-bold')}>{tableHeaders}</Tr>
            </Thead>
            <Tbody>
              {buildCenterRows({
                currentItems,
                columnNames,
                GetColumnValue,
              })}
            </Tbody>
          </Table>
          {displayPagination && (
            <div className={classnames('mt-3', 'center-listing-pagination')}>
              <ReactPaginate
                nextLabel=""
                onPageChange={handlePageClick}
                pageRangeDisplayed={3}
                marginPagesDisplayed={2}
                pageCount={pageCount}
                previousLabel=""
                pageClassName="centertype-pagination-item text-blue-morning text-body underline"
                pageLinkClassName="pagination-link"
                previousClassName="page-item"
                previousLinkClassName="pagination-prev pagination-link"
                nextClassName="page-item"
                nextLinkClassName="pagination-next pagination-link"
                breakLabel="..."
                breakClassName="centertype-pagination-item"
                breakLinkClassName="pagination-link"
                containerClassName="pagination-list"
                activeClassName="no-underline text-blue-night"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SalesforceTable;
